import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/api';
import { Settings, Lock, User, CheckCircle, AlertCircle, Palette, Upload, Image as ImageIcon, Trash2 } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800/40' },
  manager: { label: 'Manager', color: 'text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-950/30 border-cyan-200 dark:border-cyan-800/40' },
  member: { label: 'Member', color: 'text-muted-foreground bg-muted border-border' },
};

function BrandingSection() {
  const [companyName, setCompanyName] = useState('');
  const [brandColor, setBrandColor] = useState('#2563EB');
  const [hasLogo, setHasLogo] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/branding').then(r => {
      setCompanyName(r.data.company_name || '');
      setBrandColor(r.data.brand_color || '#2563EB');
      setHasLogo(!!r.data.has_logo);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await api.put('/branding', { company_name: companyName, brand_color: brandColor });
      setMsg({ type: 'success', message: 'Branding saved' });
    } catch (e) {
      setMsg({ type: 'error', message: e.response?.data?.detail || 'Failed to save branding' });
    } finally { setSaving(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/branding/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHasLogo(true); setLogoBust(Date.now());
      setMsg({ type: 'success', message: 'Logo uploaded' });
    } catch (e) {
      setMsg({ type: 'error', message: e.response?.data?.detail || 'Failed to upload logo' });
    } finally { setUploading(false); }
  };

  const removeLogo = async () => {
    try { await api.delete('/branding/logo'); setHasLogo(false); setLogoBust(Date.now()); } catch {}
  };

  return (
    <div className="border border-border rounded-sm p-5 mb-6" data-testid="branding-section">
      <h2 className="text-sm font-heading uppercase tracking-wider text-foreground/80 flex items-center gap-2 mb-1">
        <Palette className="w-4 h-4 text-muted-foreground" /> Branding
      </h2>
      <p className="text-[11px] text-muted-foreground mb-4">Used on the Workflow Guide PDF cover and client-ready hand-offs.</p>

      {msg && (
        <div className={`flex items-center gap-2 px-3 py-2.5 rounded-sm mb-4 text-xs ${
          msg.type === 'success'
            ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/40'
            : 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-400 border border-rose-300 dark:border-rose-800/40'
        }`} data-testid="branding-feedback">
          {msg.type === 'success' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
          {msg.message}
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Company Name</label>
          <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} maxLength={80}
            data-testid="branding-company-input" placeholder="e.g., Acme Builders"
            className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/40" />
        </div>

        <div>
          <label className="block text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Brand Color</label>
          <div className="flex items-center gap-3">
            <input type="color" value={brandColor} onChange={e => setBrandColor(e.target.value)}
              data-testid="branding-color-input"
              className="w-12 h-9 rounded-sm border border-border bg-muted cursor-pointer" />
            <input type="text" value={brandColor} onChange={e => setBrandColor(e.target.value)}
              className="w-28 px-3 py-2 text-sm font-mono bg-muted border border-border rounded-sm text-foreground focus:outline-none focus:border-primary/40" />
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Logo</label>
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 rounded-sm border border-border bg-muted flex items-center justify-center overflow-hidden" data-testid="branding-logo-preview">
              {hasLogo
                ? <img src={`${BACKEND}/api/branding/logo?t=${logoBust}`} alt="logo" className="w-full h-full object-contain" />
                : <ImageIcon className="w-5 h-5 text-muted-foreground" />}
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={e => uploadLogo(e.target.files?.[0])} data-testid="branding-logo-file" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid="branding-logo-upload-btn"
              className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground border border-border rounded-sm hover:text-foreground hover:border-primary/30 transition-colors disabled:opacity-50">
              <Upload className="w-3.5 h-3.5" /> {uploading ? 'Uploading...' : 'Upload Logo'}
            </button>
            {hasLogo && (
              <button onClick={removeLogo} data-testid="branding-logo-remove-btn"
                className="flex items-center gap-1 px-2 py-2 text-xs text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20 rounded-sm transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Remove
              </button>
            )}
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">PNG, JPG, or WEBP — up to 4 MB.</p>
        </div>

        <div className="pt-1">
          <button onClick={save} disabled={saving} data-testid="branding-save-btn"
            className="px-5 py-2.5 text-xs font-heading uppercase tracking-wider bg-primary/15 text-primary border border-primary/30 rounded-sm hover:bg-primary/20 transition-colors disabled:opacity-50">
            {saving ? 'Saving...' : 'Save Branding'}
          </button>
        </div>
      </div>
    </div>
  );
}


export default function SettingsPage() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setFeedback(null);

    if (newPassword.length < 6) {
      setFeedback({ type: 'error', message: 'New password must be at least 6 characters' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setFeedback({ type: 'error', message: 'New passwords do not match' });
      return;
    }

    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setFeedback({ type: 'success', message: 'Password changed successfully' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setFeedback({ type: 'error', message: err.response?.data?.detail || 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  const roleCfg = ROLE_CONFIG[user?.role] || ROLE_CONFIG.member;
  const canBrand = user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="max-w-2xl mx-auto py-8 px-6" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-xl font-heading uppercase tracking-wider text-foreground flex items-center gap-2">
          <Settings className="w-5 h-5 text-primary" /> Settings
        </h1>
        <p className="text-xs text-muted-foreground mt-1">Manage your profile, security, and branding</p>
      </div>

      {canBrand && <BrandingSection />}

      {/* Profile Info */}
      <div className="border border-border rounded-sm p-5 mb-6" data-testid="profile-section">
        <h2 className="text-sm font-heading uppercase tracking-wider text-foreground/80 flex items-center gap-2 mb-4">
          <User className="w-4 h-4 text-muted-foreground" /> Profile
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Name</p>
            <p className="text-sm text-foreground font-mono" data-testid="settings-user-name">{user?.name || '—'}</p>
          </div>
          <div>
            <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Email</p>
            <p className="text-sm text-foreground font-mono" data-testid="settings-user-email">{user?.email || '—'}</p>
          </div>
          <div>
            <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Role</p>
            <span className={`inline-block text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-sm border ${roleCfg.color}`}
              data-testid="settings-user-role">
              {roleCfg.label}
            </span>
          </div>
          <div>
            <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">Joined</p>
            <p className="text-sm text-foreground font-mono" data-testid="settings-user-joined">
              {user?.created_at ? new Date(user.created_at).toLocaleDateString() : '—'}
            </p>
          </div>
        </div>
      </div>

      {/* Change Password */}
      <div className="border border-border rounded-sm p-5" data-testid="change-password-section">
        <h2 className="text-sm font-heading uppercase tracking-wider text-foreground/80 flex items-center gap-2 mb-4">
          <Lock className="w-4 h-4 text-muted-foreground" /> Change Password
        </h2>

        {feedback && (
          <div className={`flex items-center gap-2 px-3 py-2.5 rounded-sm mb-4 text-xs ${
            feedback.type === 'success'
              ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800/40'
              : 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-400 border border-rose-300 dark:border-rose-800/40'
          }`} data-testid="password-feedback">
            {feedback.type === 'success' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
            {feedback.message}
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-3">
          <div>
            <label className="block text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              required
              data-testid="current-password-input"
              className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/40"
              placeholder="Enter current password"
            />
          </div>
          <div>
            <label className="block text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
              minLength={6}
              data-testid="new-password-input"
              className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/40"
              placeholder="Min 6 characters"
            />
          </div>
          <div>
            <label className="block text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              data-testid="confirm-password-input"
              className="w-full px-3 py-2 text-sm bg-muted border border-border rounded-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/40"
              placeholder="Re-enter new password"
            />
          </div>
          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              data-testid="change-password-btn"
              className="px-5 py-2.5 text-xs font-heading uppercase tracking-wider bg-primary/15 text-primary border border-primary/30 rounded-sm hover:bg-primary/20 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Update Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
